import {
  useRef,
  useMemo,
  useEffect,
  type DependencyList,
  type EffectCallback,
  useCallback,
} from "react";

import { isEqual } from "lodash";

/**
 * A hook that returns a value of type {@link number} that increments each time the provided value,
 * {@link T}, changes - where a change is determined by a comparison function {@link Comparator}
 * that can be provided to the hook.
 */
export function useComparedValueRef<T>(value: T) {
  const ref = useRef<T | undefined>(undefined);
  const signalRef = useRef<number>(0);

  if (!isEqual(ref.current, value)) {
    ref.current = value;
    signalRef.current += 1;
  }
  return signalRef.current;
}

/**
 * A variation of React's `useMemo` hook that performs a deep comparison rather than a value
 * comparison when determining if a value has changed.
 */
export const useDeepEqualMemo = <T>(
  callback: Parameters<typeof useMemo<T>>[0],
  dependencies: Parameters<typeof useMemo<T>>[1],
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
) => useMemo<T>(callback, [useComparedValueRef(dependencies)]);

export const useDeepEqualEffect = (
  callback: EffectCallback,
  dependencies: DependencyList,
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
) => useEffect(callback, [useComparedValueRef(dependencies)]);

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const useDeepEqualCallback = <T extends (...args: any[]) => any>(
  callback: Parameters<typeof useCallback<T>>[0],
  dependencies: DependencyList,
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
) => useCallback(callback, [useComparedValueRef(dependencies)]);
